import { Cookie } from '@tools/cookie';
import { number as formatNumber } from '@tools/number-formatter';
import { trans, transchoice } from '@tools/translation';

export type Place = { name: string | null; cityName: string | null } | null;
export type LocationItem = { place: Place } | null;

export type TeamSize = { min: number | null; max: number | null } | null;

export type PriceRange = { min: number | null; max: number | null } | null;

export type LastSearchCookie = {
  locationList?: LocationItem[] | null;
  teamSize?: TeamSize;
  price?: PriceRange;
  url?: string | null;
} | null | undefined;

export type LastSearchData = {
  fullText: string;
  labelText: string;
  url: string | null;
};

export const getLastSearchHeader = (): string => trans('recent_search.last');

const buildLastSearchLabel = (cookieValue: LastSearchCookie): { label: string | null } => {
  if (!cookieValue || typeof cookieValue !== 'object') {
    return { label: null };
  }

  let location: string | null = null;
  const names = (cookieValue.locationList || [])
    .map((item: LocationItem) => item && item.place && (item.place.name || item.place.cityName))
    .filter((s): s is string => typeof s === 'string')
    .map((s) => s) as string[];
  if (names.length) {
    location = names.join(', ');
    if (location && location.trim().toLowerCase() === 'france') {
      location = null;
    }
  }

  let team: string | null = null;
  if (cookieValue.teamSize && (cookieValue.teamSize.min || cookieValue.teamSize.max)) {
    const min = cookieValue.teamSize.min || null;
    const max = cookieValue.teamSize.max || null;
    if (min && max) {
      team = `${min} - ${transchoice('recent_search.workstations', { count: max })}`;
    } else if (max) {
      team = transchoice('recent_search.workstations', { count: max });
    } else if (min) {
      team = transchoice('recent_search.workstations', { count: min });
    }
  }

  let price: string | null = null;
  if (cookieValue.price && (cookieValue.price.min || cookieValue.price.max)) {
    const minRaw = cookieValue.price.min;
    const maxRaw = cookieValue.price.max;
    const hasMin = !!(minRaw && minRaw > 0);
    const hasMax = !!(maxRaw && maxRaw > 0);

    const labelMin = trans('global.min');
    const labelMax = trans('global.max');

    const toKLabel = (value: number): string => {
      const kValue = Math.round((value / 1000) * 10) / 10;
      return `${formatNumber(kValue)}\u202Fk`;
    };

    if (hasMin && hasMax) {
      price = `${formatNumber(minRaw / 1000)} - ${toKLabel(maxRaw)} ${trans('global.currency_symbol')}`;
    } else if (hasMin) {
      price = `${labelMin} ${toKLabel(minRaw)} ${trans('global.currency_symbol')}`;
    } else if (hasMax) {
      price = `${labelMax} ${toKLabel(maxRaw)} ${trans('global.currency_symbol')}`;
    }
  }

  const parts: string[] = [];
  if (location) {
    parts.push(location);
  }
  if (team) {
    parts.push(team);
  }
  if (price) {
    parts.push(price);
  }

  if (!parts.length) {
    return { label: null };
  }

  return { label: parts.join(' • ') };
};

export const getLastSearchData = (): LastSearchData | null => {
  let payload: LastSearchCookie = null;
  try {
    const value = (Cookie as unknown as { getJSON: (name: string) => unknown }).getJSON('recent-search-ml');
    payload = value as LastSearchCookie;
  } catch (e) {
    payload = null;
  }

  const { label } = buildLastSearchLabel(payload);
  if (!label) {
    return null;
  }

  const header = getLastSearchHeader();
  const fullText = `${header}${label}`;
  const url = (payload && typeof payload === 'object' ? (payload as LastSearchCookie)?.url ?? null : null) as string | null;

  return { fullText, labelText: label, url };
};
